import axios, { type AxiosResponse } from "axios";

import { type OAuthConnectionResponseDto } from "./types/oauth-connection-response-dto";
import { type ApiGatewayOAuthMeta } from "./types/api-gateway-oauth-meta";
import { type BaseExternalAccessMeta } from "./types/base-external-access-meta";
import { type ExternalAccessMetaType } from "./types/external-access-meta-type";
import { type CancellationToken } from "./types/cancellation-token";
import { type UserDashboard } from "./types/user-dashboard";
import { type SsoRedirectResponseDto } from "./types/sso-redirect-response-dto";
import { type GetSsoInfoResponseDto } from "./types/get-sso-info-response-dto";
import { type GetSsoInfoCompanyResponseDto } from "./types/get-sso-info-company-response-dto";
import { type GridLayouts } from "./types/grid-layouts";
import { type ValidateEntraTokenDto } from "./types/validate-entra-token-dto";
import { type TokenResponse } from "./types/token-response";
import { type LoginRequest } from "./types/login-request";
import { type TokenExpirationResponseDto } from "./types/token-expiration-response-dto";
import { type GetAdditionalTenantsRequestDto } from "./types/get-additional-tenants-request-dto";
import { type GetAdditionalTenantsResponseDto } from "./types/get-additional-tenants-response-dto";
import { type PackNetServerSettings } from "./types/pack-net-server-settings";
import { type Tenant } from "./types/tenant";
import { type TimeZoneOffsetResponse } from "./types/time-zone-offset-response";
import { type AddExternalAccessDto } from "./types/add-external-access-dto";
import { type SubscriptionDto } from "./types/subscription-dto";
import { type ApimSubscriptionMeta } from "./types/apim-subscription-meta";
import { type DeleteExternalAccessResponse } from "./types/delete-external-access-response";
import { type InitiatePasswordResetDto } from "./types/initiate-password-reset-dto";
import { type ValidatePasswordResetDto } from "./types/validate-password-reset-dto";
import { type ForgotPasswordData } from "./types/forgot-password-data";
import { type AddUserTenantDto } from "./types/add-user-tenant-dto";
import { type User } from "./types/user";
import { type UserWithPassword } from "./types/user-with-password";
import { type LicensingResponseDto } from "./types/licensing-response-dto";
import { type Role } from "./types/role";
import { type CreateCompanyRoleDto } from "./types/create-company-role-dto";
import { type GetRolesAndPermissionsDto } from "./types/get-roles-and-permissions-dto";
import { type HydratedUser } from "./types/hydrated-user";
import { type CheckFeatureFlagDto } from "./types/check-feature-flag-dto";
import { type Site } from "./types/site";
import { type UpdateLanguageDto } from "./types/update-language-dto";
import { type Company } from "./types/company";
import { type CreateOrUpdateCompanyDto } from "./types/create-or-update-company.dto";
import { type CreateTenantWithSettingsDto } from "./types/create-tenant-with-settings-dto";
import { type TenantWithSettingsResponseDto } from "./types/tenant-with-settings-response.dto";
import { type UpdateTenantPutDto } from "./types/update-tenant-put-dto";
import { type StartSsoLoginResponseDto } from "./types/start-sso-login-response-dto";

type ApiResponse<T> = {
	success: boolean;
	responseData?: T;
	status: number;
	data?: T;
	friendlyErrorMessage?: string;
	statusText?: string;
};

export class IdentityApiSdk {
	private rootUrl: string = "/identityApi/api";
	private authToken: string | null = null;

	constructor(token: string | (() => string) | null = null, baseUrl: string | (() => string) | null = null) {
		if (baseUrl != null) this.setSdkRootUrl(baseUrl);
		if (token != null) this.setSdkAuthenticationToken(token);
	}

	private formatApiReturn<T>(
		response:
			| AxiosResponse<T>
			| {
					status: number;
					data?: T;
					response?: { data?: T };
					statusText?: string;
			  },
	): ApiResponse<T> {
		const result: ApiResponse<T> = {
			success: false,
			status: response.status,
			data: undefined,
			responseData: undefined,
		};

		switch (response.status) {
			case 201:
			case 200: {
				result.success = true;
				result.responseData = "data" in response ? response.data : undefined;
				break;
			}
			case 204: {
				result.success = true;
				result.responseData = undefined;
				break;
			}
			case 403: {
				result.success = false;
				result.friendlyErrorMessage = "Access denied. You do not have the correct permissions.";
				result.responseData = "response" in response ? response.response?.data : undefined;
				break;
			}
			case 409: {
				result.success = false;
				result.friendlyErrorMessage = "Operation failed. A record already exists with that information.";
				result.responseData = "data" in response ? response.data : undefined;
				result.statusText = "statusText" in response ? response.statusText : undefined;
				break;
			}
			default: {
				result.success = false;
				result.friendlyErrorMessage = "Something went wrong, try again in a few";
				result.responseData = "data" in response ? response.data : undefined;
			}
		}

		result.status = response.status;
		result.data = result.responseData;
		return result;
	}

	private async handle<T>(callback: () => Promise<AxiosResponse<T>>): Promise<ApiResponse<T>> {
		try {
			return this.formatApiReturn<T>(await callback());
		} catch (e: unknown) {
			const error = e as {
				status?: number;
				code?: string;
				data?: T;
				response?: { data?: T };
				statusText?: string;
			};
			const response = {
				...error,
				status: error.status ?? (error.code ? parseInt(error.code, 10) : 500),
			};
			return this.formatApiReturn<T>(response);
		}
	}

	setSdkRootUrl(rootUrl: string | (() => string)): void {
		let localUrl: string;

		if (typeof rootUrl === "function") {
			localUrl = rootUrl();
		} else {
			localUrl = rootUrl;
		}
		this.rootUrl = localUrl;
	}

	getSdkRootUrl(): string {
		return this.rootUrl;
	}

	setSdkAuthenticationToken(authToken: string | (() => string)): void {
		let localToken: string;

		if (typeof authToken === "function") {
			localToken = authToken();
		} else {
			localToken = authToken;
		}

		if (!localToken.startsWith("BEARER ")) {
			localToken = "BEARER " + localToken;
		}
		this.authToken = localToken;
	}

	getSdkAuthenticationToken(): string | null {
		return this.authToken;
	}

	async getUserDashboardApi(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/Dashboard`;
		return await this.handle(
			async () =>
				await axios.get<UserDashboard>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async IsUserSsoEnabled(username: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/oauth/token/tenantSsoUrl/byUser/${username}`;
		return await this.handle(
			async () =>
				await axios.get<SsoRedirectResponseDto>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async GetUserSsoInfo(
		email: string,
		application: string,
		cancellationTokenOrReturnTo: CancellationToken | string | null = null,
		returnTo: string | null = null,
	) {
		const cancellationToken =
			typeof cancellationTokenOrReturnTo === "string" || cancellationTokenOrReturnTo === null
				? null
				: cancellationTokenOrReturnTo;
		const resolvedReturnTo = typeof cancellationTokenOrReturnTo === "string" ? cancellationTokenOrReturnTo : returnTo;
		const payload: {
			email: string;
			application: string;
			returnTo?: string;
		} = {
			email,
			application,
		};

		if (resolvedReturnTo !== null && resolvedReturnTo !== undefined) {
			payload.returnTo = resolvedReturnTo;
		}

		const url = `${this.rootUrl}/v1/GetSsoInfo`;
		return await this.handle(
			async () =>
				await axios.post<GetSsoInfoResponseDto | GetSsoInfoCompanyResponseDto>(url, payload, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async startSsoLogin(connection: string, application: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/sso/startLogin`;

		const requestDto = {
			application,
			connection,
		};

		return await this.handle(
			async () =>
				await axios.post<StartSsoLoginResponseDto>(url, requestDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async saveDashboardLayoutApi(layout: GridLayouts, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/Dashboard/layouts`;
		return await this.handle(
			async () =>
				await axios.put<void>(url, layout, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async saveSelectedWidgetsApi(selectedWidgets: string[], cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/Dashboard/selectedWidgets`;
		return await this.handle(
			async () =>
				await axios.put<void>(url, selectedWidgets, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async validateEnterpriseSsoToken(token: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/oauth/token/validateEntraToken`;
		const dto: ValidateEntraTokenDto = {
			entraToken: token,
		};
		return await this.handle(
			async () =>
				await axios.post<TokenResponse>(url, dto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async requestToken(userName: string, password: string, cancellationToken: CancellationToken | null = null) {
		const loginRequest: LoginRequest = {
			userName,
			password,
		};
		return await this.handle(
			async () =>
				await axios.post<TokenResponse>(`${this.rootUrl}/v1/oauth/token/acquire`, loginRequest, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getTokenExpiry(cancellationToken: CancellationToken | null = null) {
		return await this.handle(
			async () =>
				await axios.get<TokenExpirationResponseDto>(`${this.rootUrl}/v1/oauth/token/expiresInSeconds`, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async GetAdditionalTenants(dto: GetAdditionalTenantsRequestDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/GetAdditionalTenants`;
		return await this.handle(
			async () =>
				await axios.post<GetAdditionalTenantsResponseDto>(url, dto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getSettings(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/Settings`;
		return await this.handle(
			async () =>
				await axios.get<PackNetServerSettings>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async updateSettings(dto: PackNetServerSettings, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/Settings/${dto.id}`;
		return await this.handle(
			async () =>
				await axios.put<PackNetServerSettings>(url, dto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getTenants(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants`;
		return await this.handle(
			async () =>
				await axios.get<Tenant[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getTenantTimezone(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/timeZoneOffset`;
		return await this.handle(
			async () =>
				await axios.get<TimeZoneOffsetResponse>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async createApimSubscription(dto: AddExternalAccessDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/externalAccess/apim`;
		return await this.handle(
			async () =>
				await axios.post<SubscriptionDto>(url, dto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getApimSubscriptions(tenantId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/GetApimSubscriptionsByTenant/${tenantId}`;
		return await this.handle(
			async () =>
				await axios.get<ApimSubscriptionMeta[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async removeApimSubscription(subscriptionId: string, cancellationToken: CancellationToken | null = null) {
		if (!subscriptionId) {
			throw new Error("subscriptionId is required to delete an APIM subscription");
		}
		const url = `${this.rootUrl}/v1/externalAccess/${subscriptionId}`;
		return await this.handle(
			async () =>
				await axios.delete<DeleteExternalAccessResponse>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async initiatePasswordReset(
		passwordResetDto: InitiatePasswordResetDto,
		cancellationToken: CancellationToken | null = null,
	) {
		const url = `${this.rootUrl}/v1/users/InitiatePasswordReset`;
		return await this.handle(
			async () =>
				await axios.post<void>(url, passwordResetDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async validatePasswordResetOtp(otpDto: ValidatePasswordResetDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/ValidatePasswordResetOtp`;
		return await this.handle(
			async () =>
				await axios.post<TokenResponse>(url, otpDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async forgotPasswordReset(resetPasswordDto: ForgotPasswordData, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/ForgotPasswordReset`;
		return await this.handle(
			async () =>
				await axios.post<void>(url, resetPasswordDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async addUserTenant(addUserTenantDto: AddUserTenantDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/AddUserTenant`;
		return await this.handle(
			async () =>
				await axios.post<User>(url, addUserTenantDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getUsers(cancellationToken: CancellationToken | null = null) {
		// TODO Endpoint is /v1/users but swagger shows /v1/users/all - may need verification
		const url = `${this.rootUrl}/v1/users`;
		return await this.handle(
			async () =>
				await axios.get<User[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async addUser(user: UserWithPassword, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users`;
		return await this.handle(
			async () =>
				await axios.post<User>(url, user, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async removeUser(userId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/${userId}`;
		return await this.handle(
			async () =>
				await axios.delete<void>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async updateUserWithId(
		user: Omit<UserWithPassword, "lastLoginTime" | "defaultDesignId">,
		cancellationToken: CancellationToken | null = null,
	) {
		const url = `${this.rootUrl}/v1/users/${user.id}`;
		return await this.handle(
			async () =>
				await axios.put<User>(url, user, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async updateUserWithoutPassword(user: User, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/${user.id}/withoutPassword`;
		return await this.handle(
			async () =>
				await axios.put<User>(url, user, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getLicensingInfo(tenantId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/GetLicensing/${tenantId}`;
		return await this.handle(
			async () =>
				await axios.get<LicensingResponseDto>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async refreshToken(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/oauth/token/refresh`;
		return await this.handle(
			async () =>
				await axios.post<TokenResponse>(
					url,
					{},
					{
						headers: { Authorization: this.authToken },
						cancelToken: cancellationToken?.token,
					},
				),
		);
	}

	async getSite(tenantId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/oauth/token/site/${tenantId}`;
		return await this.handle(
			async () =>
				await axios.get<TokenResponse>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getSites(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/sites`;
		return await this.handle(
			async () =>
				await axios.get<Tenant[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getRoles(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/roles`;
		return await this.handle(
			async () =>
				await axios.get<Role[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async addRole(data: Role, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/roles`;
		return await this.handle(
			async () =>
				await axios.post<Role>(url, data, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async updateRole(data: Role, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/roles/${data.id}`;
		return await this.handle(
			async () =>
				await axios.put<Role>(url, data, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async removeRole(id: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/roles/${id}`;
		return await this.handle(
			async () =>
				await axios.delete<void>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getCompanyRoles(companyId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/companies/${companyId}/roles`;
		return await this.handle(
			async () =>
				await axios.get<Role[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async createCompanyRole(
		companyId: string,
		data: CreateCompanyRoleDto,
		cancellationToken: CancellationToken | null = null,
	) {
		const url = `${this.rootUrl}/v1/companies/${companyId}/roles`;
		return await this.handle(
			async () =>
				await axios.post<Role>(url, data, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async updateCompanyRole(companyId: string, data: Role, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/companies/${companyId}/roles/${data.id}`;
		return await this.handle(
			async () =>
				await axios.put<Role>(url, data, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async deleteCompanyRole(companyId: string, id: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/companies/${companyId}/roles/${id}`;
		return await this.handle(
			async () =>
				await axios.delete<void>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getUserPermissions(userId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/permissions/${userId}`;
		return await this.handle(
			async () =>
				await axios.get<GetRolesAndPermissionsDto>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getUserFeatureFlags(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/featureflags`;
		return await this.handle(
			async () =>
				await axios.get<Record<string, string>>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async hydrateUser(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/hydrateUser`;
		return await this.handle(
			async () =>
				await axios.get<HydratedUser>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getTenantsBySalesForceId(salesForceId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/GetTenantsBySalesForceId/${btoa(salesForceId)}`;
		return await this.handle(
			async () =>
				await axios.get<Tenant>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async checkFeatureFlag(checkFlagDto: CheckFeatureFlagDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/checkFeatureFlag`;
		return await this.handle(
			async () =>
				await axios.post<boolean>(url, checkFlagDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getKioskSites(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/kiosk/sites`;
		return await this.handle(
			async () =>
				await axios.get<Site[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getKioskVersion(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/kiosk/sites/version`;
		return await this.handle(
			async () =>
				await axios.get<string>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getAllFeatureFlags(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/getAllFeatureFlags`;
		return await this.handle(
			async () =>
				await axios.post<Record<string, boolean>>(url, null, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async updateUserLanguage(dto: UpdateLanguageDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/updateLanguage`;
		return await this.handle(
			async () =>
				await axios.post<User>(url, dto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getCompany(companyId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/companies/${companyId}`;
		return await this.handle(
			async () =>
				await axios.get<Company>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getCompanies(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/companies`;
		return await this.handle(
			async () =>
				await axios.get<Company[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async createOrUpdateCompany(company: CreateOrUpdateCompanyDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/companies`;
		return await this.handle<Company>(
			async () =>
				await axios.post<Company>(url, company, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async deleteCompany(companyId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/companies/${companyId}`;
		return await this.handle(
			async () =>
				await axios.delete<void>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async createTenantWithSettings(dto: CreateTenantWithSettingsDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/with-settings`;
		return await this.handle(
			async () =>
				await axios.post<TenantWithSettingsResponseDto>(url, dto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async deleteTenant(tenantId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/${tenantId}`;
		return await this.handle(
			async () =>
				await axios.delete<void>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getTenantById(tenantId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/${tenantId}`;
		return await this.handle(
			async () =>
				await axios.get<Tenant>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async updateTenant(updateTenantDto: UpdateTenantPutDto, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/${updateTenantDto.tenantId}`;
		return await this.handle(
			async () =>
				await axios.put<Tenant>(url, updateTenantDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getTenantWithSettings(tenantId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/tenants/${tenantId}/with-settings`;
		return await this.handle(
			async () =>
				await axios.get<TenantWithSettingsResponseDto>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getUserTenants(cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/users/tenants`;
		return await this.handle(
			async () =>
				await axios.get<Tenant[]>(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async createOAuthConnection(
		externalAccessDto: AddExternalAccessDto,
		cancellationToken: CancellationToken | null = null,
	) {
		const url = `${this.rootUrl}/v1/externalAccess/oauth`;
		return await this.handle(
			async () =>
				await axios.post<OAuthConnectionResponseDto>(url, externalAccessDto, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async getExternalAccessMeta(
		metaType: "Apim",
		cancellationToken?: CancellationToken | null,
	): Promise<ApiResponse<ApimSubscriptionMeta[]>>;
	async getExternalAccessMeta(
		metaType: "OAuth",
		cancellationToken?: CancellationToken | null,
	): Promise<ApiResponse<ApiGatewayOAuthMeta[]>>;
	async getExternalAccessMeta(
		metaType?: "All" | null,
		cancellationToken?: CancellationToken | null,
	): Promise<ApiResponse<BaseExternalAccessMeta[]>>;
	async getExternalAccessMeta(
		metaType?: ExternalAccessMetaType | null,
		cancellationToken?: CancellationToken | null,
	): Promise<ApiResponse<BaseExternalAccessMeta[] | ApimSubscriptionMeta[] | ApiGatewayOAuthMeta[]>> {
		const url = `${this.rootUrl}/v1/externalAccess?type=${metaType ?? "All"}`;
		return await this.handle(
			async () =>
				await axios.get(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}

	async deleteOAuthConnection(externalAccessId: string, cancellationToken: CancellationToken | null = null) {
		const url = `${this.rootUrl}/v1/externalAccess/${externalAccessId}`;
		return await this.handle(
			async () =>
				await axios.delete(url, {
					headers: { Authorization: this.authToken },
					cancelToken: cancellationToken?.token,
				}),
		);
	}
}
